/**
 * The helpers every screen uses to show a refused request (BLP/nx#2813 K1):
 * the error message, the field errors of a write, the `saved_then_failed`
 * check, the HTTP status checks and the error toast with „Fehler-ID
 * kopieren“. Moved from erp's `composables/apiError.ts`, which re-exports
 * them, so the other apps can use them too.
 *
 * Nothing here imports axios — the package does not depend on it. An axios
 * error is recognised by the flag axios sets on it (`isAxiosError`), exactly
 * as `axios.isAxiosError` does.
 */
// Type only: the kit's barrel pulls its editor (squire-rte), which needs a DOM
// at import time, and this file is imported far down into DOM-less code.
import type { NxToastOptions } from '@blpde/nx-ui';
import { isJsonContentType, readProblem, type Problem, type ProblemFieldError } from './problem';
import { targetNames } from './fieldErrorBinding';

/** A `t` of vue-i18n as the helpers call it: a key and its named parameters. */
export type Translate = (key: string, params?: Record<string, unknown>) => string;

/** `useNxToasts().showToast` in its options form. */
export type ShowToast = (options: NxToastOptions) => number;

/** The toast host a helper shows its toasts on and takes them back from: `useNxToasts()`. */
export interface ToastHost {
  showToast: ShowToast;
  dismiss: (id: number) => void;
}

/** What an axios error carries that the helpers read. */
interface AxiosLikeError {
  isAxiosError: true;
  message?: unknown;
  response?: {
    status?: number;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
}

/** Whether e is an axios error — the test of `axios.isAxiosError`, without importing axios. */
function isAxiosLike(e: unknown): e is AxiosLikeError {
  return (
    typeof e === 'object' && e !== null && (e as { isAxiosError?: unknown }).isAxiosError === true
  );
}

/**
 * The error message of a failed request, fit for a screen.
 *
 * Delegates to `readProblem`, the one reader of every error shape the services
 * answer: the problem shape — its `detail` is rendered by the backend in the
 * language the request asked for — and the older ones:
 *   - erp nested:  { error: { code, message } }
 *   - flat:        { status, code, message }
 *   - authz:       { error: "unauthenticated" }
 * A refused GraphQL operation (`GraphQLRequestError`, HTTP 200) is read from
 * its envelope errors the same way: `message` is the detail, `extensions` the
 * code, request id, field errors and hints. An answer without a readable text
 * (a proxy's HTML page, an empty body) and a failure without an answer
 * (network, a request that could not be built) fall back to the error's own
 * message — the one place that reads it (spec I-2813 rule 1): there is no
 * other text to show.
 */
export function extractErrorMessage(e: unknown): string {
  const problem = readProblem(e);
  const text = problem?.detail || problem?.title;
  if (text) return text;
  if (e instanceof Error) return e.message;
  if (isAxiosLike(e) && typeof e.message === 'string') return e.message;
  return String(e);
}

/**
 * The sentence a refused request may show (spec I-2813 rule 1): the backend's,
 * read off its answer, or {@link extractErrorMessage}'s for a request without
 * one. `undefined` for anything else — a layer that threw on an answer it
 * could not use (K29: `createSalesQuoteLine answered without the created
 * row`), a bug — whose message is a developer's sentence, never a screen's:
 * the caller shows its house text and leaves the error to the console.
 */
export function refusalReason(e: unknown): string | undefined {
  if (!readProblem(e) && !isNoResponse(e)) return undefined;
  return extractErrorMessage(e) || undefined;
}

/**
 * The record an input belongs to, as a problem's `target` names it: the
 * catalog resource (`colors`, `salesOrdersLines` — the grid descriptor's
 * `resource`) and the record's id.
 */
export type FieldErrorRecord = {
  resource: string;
  id: number | string;
};

/**
 * The field error a failed write reports for one field, bound by the field's
 * key — the dotted key the input writes (`code`, `lines.3.quantity`).
 *
 * Every shape binds the same way: a problem's `pointer` by its dotted key
 * (`/code` → `code`), erp's legacy `error.fields` map and `error.duplicateFields`
 * list by the key they name, the reads' `error.fields[].path` likewise. A
 * `target` names a field of a record that was not in the request body, by
 * resource and id: it binds only when the caller says which record the input
 * belongs to (`record`), and only when BOTH match. Ids are per table, so a
 * child row (`salesOrdersLines` 12) can carry the head's id (`salesOrders` 12);
 * the resource keeps its field error away from the head's input of the same
 * name. Id 0 names no record — the backend's target for a row whose id it does
 * not know — and never binds.
 *
 * `undefined` when the answer names no such field, or is no HTTP answer at all
 * — a field is never inferred from an error text.
 */
export function fieldErrorFor(
  e: unknown,
  key: string,
  record?: FieldErrorRecord
): ProblemFieldError | undefined {
  return fieldErrorsFor(e, key, record)[0];
}

/**
 * Every field error a failed write names for one field, bound like
 * {@link fieldErrorFor}: the ones by key first, then the ones by target. An
 * answer may name a field more than once (master's `errors` map carries a
 * list per field), and an input shows all of them.
 */
export function fieldErrorsFor(
  e: unknown,
  key: string,
  record?: FieldErrorRecord
): ProblemFieldError[] {
  const fieldErrors = readProblem(e)?.fieldErrors ?? [];
  const byKey = fieldErrors.filter((fieldError) => fieldError.key === key);
  if (!record) return byKey;
  return byKey.concat(
    fieldErrors.filter(
      (fieldError) => !!fieldError.target && targetNames(fieldError.target, key, record)
    )
  );
}

/**
 * The texts a failed write names for the fields it sent, for an input's error
 * state: key → the field errors' own `detail`s, one after the other, else the
 * problem's (a legacy `duplicateFields` entry carries none). Only the keys
 * given — the fields the request carried: a composite key's partner the user
 * did not touch stays unmarked (#1817). Bound like {@link fieldErrorsFor}: by
 * pointer, and by target when the caller names the inputs' `record`; empty
 * for a failure that names no field.
 */
export function fieldErrorTexts(
  e: unknown,
  keys: Iterable<string>,
  record?: FieldErrorRecord
): Map<string, string> {
  const texts = new Map<string, string>();
  for (const key of keys) {
    const fieldErrors = fieldErrorsFor(e, key, record);
    if (fieldErrors.length === 0) continue;
    const own = fieldErrors.map((fieldError) => fieldError.detail).filter(Boolean);
    const text = own.length > 0 ? own.join(' ') : extractErrorMessage(e);
    if (text) texts.set(key, text);
  }
  return texts;
}

/**
 * The code of a write that failed after its transaction committed (500,
 * spec I-2637 K30): the change may have been saved anyway. Its detail says so
 * and asks to reload before repeating.
 */
export const SAVED_THEN_FAILED_CODE = 'saved_then_failed';

/**
 * True when a failed write may have been saved anyway (`saved_then_failed`, on
 * REST as well as on a GraphQL mutation field). A screen that sends a write by
 * itself — a draft created on focusout, a retry on the next change — must not
 * send it again: without a unique key it would create a second record, with
 * one its `duplicate_value` would replace the "possibly saved" sentence. Stop
 * the automatic submission until the page is reloaded, and offer the reload.
 * The erp axios clients never retry a 500 (`api.ts`), nor does `gql()` a
 * mutation.
 */
export function isSavedThenFailed(e: unknown): boolean {
  return readProblem(e)?.code === SAVED_THEN_FAILED_CODE;
}

/**
 * Makes the refusal of a file request readable for `readProblem`. A request
 * with `responseType: 'blob'` receives its error body as a Blob as well, which
 * cannot be read synchronously: the screen would show axios' "Request failed
 * with status code 404" instead of the backend's sentence. A JSON body — the
 * problem, `application/problem+json` — is replaced by its text, which
 * `readProblem` parses like any other answer; the error stays the same axios
 * error, with its status and request id. Any other body (a PDF, an unreadable
 * Blob) and any non-axios error stay as they came. Always resolves with `e`,
 * so a catch block can `throw await unwrapBlobError(e)`.
 */
export async function unwrapBlobError(e: unknown): Promise<unknown> {
  const response = isAxiosLike(e) ? e.response : undefined;
  const body: unknown = response?.data;
  if (!response || !(body instanceof Blob)) return e;
  const header: unknown = response.headers?.['content-type'];
  if (!isJsonContentType(typeof header === 'string' ? header : body.type)) return e;
  try {
    response.data = await body.text();
  } catch {
    // An unreadable body stays a Blob: the error's own message is shown.
  }
  return e;
}

/** The problem a failed blob request answered with, or undefined for a non-axios error. */
export async function readProblemFromBlob(e: unknown): Promise<Problem | undefined> {
  return readProblem(await unwrapBlobError(e));
}

/**
 * Puts the request id on the clipboard, inside the click that asked for it
 * (the write starts before the first await). The kit's `copyToClipboard`
 * cannot be imported here (see the import above). Resolves false when nothing
 * was copied — no Clipboard API (an insecure context) or a refusal — so the
 * caller can show the id another way (spec I-2813 K21).
 */
export async function copyRequestId(requestId: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(requestId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Shows a failed request as an error toast: the backend's error message — or
 * `message`, when the caller phrases the failure itself — and, when the answer
 * carries a request id, the action „Fehler-ID kopieren“, which puts the id on
 * the clipboard. Support finds the log line of the failure by it.
 *
 * The toast is persistent and dismissible (spec I-2813 K13): it is the only
 * place the sentence lives, so it stays until the user closes it. Copying the
 * id does not answer the error and keeps the toast open (`keepOpen`); when the
 * clipboard refuses, a second persistent toast shows the id.
 *
 * `showToast` is `useNxToasts().showToast`, `t` the caller's `useI18n().t`:
 * this file runs outside a component setup and owns neither.
 */
export function showProblemToast(
  e: unknown,
  showToast: ShowToast,
  t: Translate,
  message: string = extractErrorMessage(e)
): number {
  const requestId = readProblem(e)?.requestId;
  if (!requestId) return showToast({ message, kind: 'error', persistent: true });
  return showToast({
    message,
    kind: 'error',
    persistent: true,
    action: {
      label: t('connect.writeFailure.copyRequestId'),
      keepOpen: true,
      onClick: () =>
        void copyRequestId(requestId).then((copied) => {
          if (copied) return;
          showToast({
            message: t('connect.writeFailure.copyFailed', { requestId }),
            kind: 'error',
            persistent: true,
          });
        }),
    },
  });
}

/** True only for a real HTTP 404 — a 500 must not look like "record missing". */
export function isHttpNotFound(e: unknown): boolean {
  return isAxiosLike(e) && e.response?.status === 404;
}

/**
 * True when the request never got an HTTP answer — server down, network gone,
 * CORS/proxy refusal. {@link extractErrorMessage} can only hand back axios' own
 * English "Network Error" for that, so a screen that wants to say it in its own
 * words tests this first.
 */
export function isNoResponse(e: unknown): boolean {
  return isAxiosLike(e) && !e.response;
}

/**
 * True only for a real HTTP 422 — the status the nx controllers answer when a
 * write was understood but a business rule refused it.
 *
 * The sibling of {@link isHttpNotFound}, and it exists for the same reason: a
 * screen that wants to NAME its refusal must not name it for a 403, a 500 or a
 * dead network. Which rule refused is the answer's error code (`readProblem`,
 * BLP/nx#2637), and its `detail` is already the sentence in the UI language —
 * a screen that reacts to one refusal switches on that code, never on a text.
 */
export function isHttpUnprocessable(e: unknown): boolean {
  return isAxiosLike(e) && e.response?.status === 422;
}

/**
 * A server refusal fit for a screen. erp's problem answers (BLP/nx#2637) carry
 * no call-site segments: their `detail` is a sentence of the error catalog, and
 * a sentence that starts with a capital passes through untouched. What this
 * still strips is an answer in an older shape that carries a wrapped Go error
 * string — `company_deviations.UpsertEmbedded: company_deviations: day …
 * already carries …` — whose leading call-site segments are for the log, not
 * the user.
 *
 * Two shapes are stripped, and the difference matters:
 *   - an IDENTIFIER segment (`company_deviations`, `customer_files.SoftDelete(5)`)
 *     — snake_case, optional `.Ident` chain, optional call argument — is stripped
 *     wherever it stands in the chain, because nothing but code writes one;
 *   - a spaced PHRASE segment (`company deviation span: …`) only when it HEADS
 *     the message. ⚠️ Deeper in, a colon after lowercase words belongs to the
 *     sentence, not to a call site: `customer_files.SoftDelete(5): cannot be
 *     deleted: open positions` must keep `cannot be deleted:`.
 * A segment carrying a digit is content, never a prefix — so the 409's day list
 * (`company deviation span 2026-12-27..`) survives whole.
 *
 * Truncation is the display cap decided for I-1738: the 409 of a fully occupied
 * 366-day span is ~15 KB of text; the structured day list lives in the mask's
 * pre-check, the server line only needs to be readable.
 */
export function cleanServerMessage(message: string, maxLength = 300): string {
  const IDENT_PREFIX = /^[a-z][a-z_]*(?:\.[A-Za-z][A-Za-z0-9_]*)*(?:\([^)]*\))?:\s+/;
  const PHRASE_PREFIX = /^[a-z][a-z_ ]*(?:\([^)]*\))?:\s+/;
  let out = message.trim();
  const head = PHRASE_PREFIX.exec(out);
  if (head) out = out.slice(head[0].length);
  for (let m = IDENT_PREFIX.exec(out); m; m = IDENT_PREFIX.exec(out)) out = out.slice(m[0].length);
  if (out.length > maxLength) out = `${out.slice(0, maxLength - 1)}…`;
  return out;
}
